import Call from "./call";

/**
 * 店铺登录态统一口径：只认本机配置（与店铺列表一致）。
 * - 抖店(source_type=1)：doudian-login
 * - 微小店(source_type=2)：config/weixiaodian/weixiaodian-{id}
 * 线上 is_login 不可信，选店下拉 / 业务拉店都应走这里。
 */

export function isDouDianShopLocalLogin(shopId) {
    if (shopId == null || shopId === "") {
        return false;
    }
    try {
        const cf = Call.sendSync("fileEvent.readDouDianLoginConfig", shopId);
        return !!(cf && cf.is_login == 1);
    } catch (e) {
        return false;
    }
}

export function isWeiXiaoDianShopLocalLogin(shopId) {
    if (shopId == null || shopId === "") {
        return false;
    }
    try {
        const cf = Call.sendSync(
            "fileEvent.readConfig",
            "config/weixiaodian/weixiaodian-" + shopId,
            true
        );
        return !!(cf && cf.is_login == 1);
    } catch (e) {
        return false;
    }
}

/** 按店铺类型判断本地是否已登录 */
export function isShopLocalLogin(shop) {
    if (!shop || shop.id == null || shop.id === "") {
        return false;
    }
    if (shop.source_type == 2) {
        return isWeiXiaoDianShopLocalLogin(shop.id);
    }
    return isDouDianShopLocalLogin(shop.id);
}

/**
 * 抖店开放平台授权是否有效（service_end_time 为 Unix 秒）
 * 0 / 缺省 / 已过期 → 未授权
 */
export function isDouDianShopAuthorized(shop, nowSec) {
    if (!shop) return false;
    const end = Number(shop.service_end_time || 0);
    if (!end) return false;
    const now =
        nowSec != null ? Number(nowSec) : Math.floor(Date.now() / 1000);
    return end >= now;
}

/** 只保留抖店且授权未过期的店铺 */
export function filterDouDianAuthorizedShops(list, nowSec) {
    const now =
        nowSec != null ? Number(nowSec) : Math.floor(Date.now() / 1000);
    return (list || []).filter(
        (s) => s && Number(s.source_type) === 1 && isDouDianShopAuthorized(s, now)
    );
}

/** 未授权/已过期时的统一提示 */
export const DOUDIAN_AUTH_REQUIRED_MSG =
    "店铺未授权或授权已过期，请先订购并授权后再使用";

/** 斗店账号列表路由（引导去授权） */
export const DOUDIAN_AUTH_LIST_ROUTE = "admin_doushops";

/**
 * 校验抖店授权；未通过返回提示文案，通过返回空字符串
 * @param {object} shop
 * @param {string} [shopName] 可选，用于拼进文案
 */
export function getDouDianAuthFailMsg(shop, shopName) {
    if (isDouDianShopAuthorized(shop)) return "";
    const name =
        shopName ||
        (shop && (shop.shop_name || shop.name)) ||
        "";
    if (name) {
        return "店铺【" + name + "】未授权或已过期，请先前往「斗店账号列表」订购并授权后再使用";
    }
    return DOUDIAN_AUTH_REQUIRED_MSG;
}

/** 选店下拉展示名：未授权追加标记 */
export function formatDouDianShopOptionLabel(shop) {
    if (!shop) return "";
    const name = shop.shop_name || shop.name || String(shop.id || "");
    if (Number(shop.source_type) === 1 && !isDouDianShopAuthorized(shop)) {
        return name + "（未授权）";
    }
    return name;
}

/** 选店是否应置灰（抖店且未授权） */
export function isDouDianShopAuthDisabled(shop) {
    if (!shop) return true;
    if (Number(shop.source_type) !== 1) return false;
    return !isDouDianShopAuthorized(shop);
}

/** 未授权抖店数量 */
export function countUnauthorizedDouDianShops(list) {
    return (list || []).filter(
        (s) => s && Number(s.source_type) === 1 && !isDouDianShopAuthorized(s)
    ).length;
}

/** 从列表中只保留已授权抖店 */
export function pickAuthorizedDouDianShops(list) {
    return filterDouDianAuthorizedShops(list);
}

/**
 * 用本地登录态覆盖/补齐 list[].is_login；requireLogin=true 时只保留已登录
 * 抖店、微小店分别按各自本地配置判断，避免微小店被抖店口径误筛掉
 */
export function applyDouDianLocalLogin(list, options) {
    const requireLogin = !!(options && options.requireLogin);
    const arr = list || [];
    const out = [];
    for (let i = 0; i < arr.length; i++) {
        const shop = arr[i];
        const loggedIn = isShopLocalLogin(shop);
        const row = Object.assign({}, shop, { is_login: loggedIn ? 1 : 0 });
        if (requireLogin && !loggedIn) {
            continue;
        }
        out.push(row);
    }
    return out;
}

/**
 * 拉取抖店/微小店账号：服务端不按 is_login 过滤，再按本地登录态筛选
 * @param {Function} apiPost (url, data) => Promise
 * @param {object} options requireLogin/requireAuthorized/status/page/pageSize/source_type 及其他透传参数
 *   - requireAuthorized=true：只保留抖店且 service_end_time 未过期（订购+授权有效）
 */
export async function fetchDouDianAccounts(apiPost, options) {
    options = options || {};
    const requireLogin = options.requireLogin !== false;
    const requireAuthorized = !!options.requireAuthorized;
    const status = options.status != null ? options.status : 1;
    const page = options.page != null ? options.page : 1;
    const pageSize = options.pageSize != null ? options.pageSize : 1000;
    const rest = Object.assign({}, options);
    delete rest.requireLogin;
    delete rest.requireAuthorized;
    delete rest.status;
    delete rest.page;
    delete rest.pageSize;

    const res = await apiPost("/admintooltaobaoaccount/getdoudianaccounts", Object.assign({
        page,
        pageSize,
        is_login: -1,
        status,
    }, rest));
    if (!res || res.code != 1) {
        return res;
    }
    let list = applyDouDianLocalLogin(res.list || [], { requireLogin });
    if (requireAuthorized) {
        list = filterDouDianAuthorizedShops(list);
    }
    return Object.assign({}, res, { list });
}
